/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Star, CheckCircle2, Sparkles, Tag, Eye, MessageCircle, MapPin, ChevronRight,
  Instagram, Twitter, Facebook, Menu, X,
} from "lucide-react";
import { ServiceIcon } from "@/lib/serviceIcons";
import { slugifyTr, LOCAL_SERVICE_LIST, MAJOR_CITIES } from "@/lib/slug";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import PakLogo from "@/components/PakLogo";
import UserMenu from "@/components/UserMenu";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { FAQS } from "@/data/mockData";
import { LANDING_TEXTS_DEFAULT, LANDING_SECTIONS_DEFAULT } from "@/lib/landingContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compute a "from price" for a service: use 1+1 base price for ana, else sabit_fiyat
function svcFromPrice(s) {
  if (s.tip === "ana") {
    const sp = s.size_prices || {};
    const candidates = Object.values(sp).filter((v) => typeof v === "number" && v > 0);
    return candidates.length ? Math.min(...candidates) : 0;
  }
  return s.sabit_fiyat || 0;
}

const HERO_IMAGE = "https://images.pexels.com/photos/8146213/pexels-photo-8146213.jpeg";

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [landing, setLanding] = useState(null); // gerçek vitrin verisi (/api/landing)
  // Düzenlenebilir içerik (admin panelinden) — varsayılanla başlar, sunucudan gelir.
  const [texts, setTexts] = useState(LANDING_TEXTS_DEFAULT);
  const [sections, setSections] = useState(LANDING_SECTIONS_DEFAULT);
  const t = (k) => texts[k] ?? LANDING_TEXTS_DEFAULT[k] ?? "";

  // Önizleme modu (?preview=1): admin editöründen postMessage ile taslak içerik gelir;
  // yayınlanmamış değişiklikler burada canlı görünür.
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  useEffect(() => {
    if (!isPreview) return;
    const onMsg = (e) => {
      const d = e.data;
      if (!d || d.type !== "pak-preview-content") return;
      if (d.texts) setTexts({ ...LANDING_TEXTS_DEFAULT, ...d.texts });
      if (Array.isArray(d.sections) && d.sections.length) setSections(d.sections);
    };
    window.addEventListener("message", onMsg);
    // Ebeveyne "hazırım" de → ilk taslağı göndersin.
    try { window.parent && window.parent.postMessage({ type: "pak-preview-ready" }, "*"); } catch (_) { /* ignore */ }
    return () => window.removeEventListener("message", onMsg);
  }, [isPreview]);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/services`)
      .then((r) => {
        if (!active) return;
        const cats = (r.data || []).map((s, idx) => ({
          key: s.id,
          label: s.ad,
          fromPrice: svcFromPrice(s),
          desc: s.aciklama,
          icon: s.ikon,
          featured: s.tip === "ana" && idx === 0,
        }));
        setCategories(cats);
      })
      .catch(() => {});
    axios.get(`${API}/landing`)
      .then((r) => { if (active) setLanding(r.data); })
      .catch(() => {});
    if (!isPreview) {
      axios.get(`${API}/content/landing`)
        .then((r) => {
          if (!active || !r.data) return;
          if (r.data.texts) setTexts({ ...LANDING_TEXTS_DEFAULT, ...r.data.texts });
          if (Array.isArray(r.data.sections) && r.data.sections.length) setSections(r.data.sections);
        })
        .catch(() => {});
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featured = landing?.featured_providers || [];
  const realReviews = landing?.reviews || [];
  const stats = landing?.stats || null;

  // ---- Bölüm render fonksiyonları (anahtar bazlı; sıra/görünürlük yönetimden gelir) ----
  const renderers = {
    hero: () => (
      <section key="hero" className="relative overflow-hidden">
        <div className="absolute inset-0 pak-grid-bg opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative">
          <div className="pak-animate-in">
            <Badge className="rounded-full bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/10 px-4 py-1.5 font-medium" data-testid="hero-badge">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> {t("hero_badge")}
            </Badge>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#0F172A]">
              {t("hero_title_1")} <br />
              <span className="text-[#10B981]">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/rezervasyon" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto h-14 rounded-2xl bg-[#10B981] hover:bg-[#0EA371] text-white px-8 text-base font-semibold shadow-lg shadow-emerald-500/20" data-testid="hero-book-btn">
                  {t("hero_cta")}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="h-14 rounded-2xl border-slate-200 hover:border-[#10B981] hover:text-[#10B981] px-8 text-base" asChild>
                <a href="#nasil" data-testid="hero-learn-btn">{t("hero_secondary")}</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
              {stats && stats.review_count >= 3 && (
                <span className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <strong className="text-[#0F172A]">{stats.avg_rating}/5</strong>
                  <span className="text-slate-500">({stats.review_count} yorum)</span>
                </span>
              )}
              {stats && stats.completed_jobs >= 10 && (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#22C55E]" />
                  {stats.completed_jobs.toLocaleString("tr-TR")}+ tamamlanan hizmet
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-[#10B981]" />
                Adli Sicil Kontrolü
              </span>
            </div>
          </div>

          <div className="relative pak-animate-in">
            <div className="absolute -top-6 -right-6 w-72 h-72 bg-[#10B981]/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative rounded-[2rem] overflow-hidden pak-shadow-lg border border-white">
              <img src={HERO_IMAGE} alt="Temiz ve modern bir oturma odası" className="w-full h-[420px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-4 md:-left-10 bg-white rounded-2xl pak-shadow-lg p-4 flex items-center gap-3 max-w-[260px]">
              <div className="h-11 w-11 rounded-full bg-emerald-50 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-[#10B981]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ücretsiz Talep</p>
                <p className="text-sm font-semibold text-[#0F172A]">Dakikalar içinde teklif</p>
              </div>
            </div>
            {featured.length > 0 && (
              <div className="hidden md:flex absolute top-8 -right-4 bg-white rounded-2xl pak-shadow-lg p-4 items-center gap-3">
                <div className="flex -space-x-2">
                  {featured.slice(0, 3).map((p) => (
                    p.photo ? (
                      <img key={p.id} src={p.photo} alt={p.name} className="h-8 w-8 rounded-full border-2 border-white object-cover" />
                    ) : (
                      <div key={p.id} className="h-8 w-8 rounded-full border-2 border-white bg-emerald-100 text-[#10B981] text-xs font-semibold flex items-center justify-center">
                        {(p.name || "?").charAt(0)}
                      </div>
                    )
                  ))}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Doğrulanmış</p>
                  <p className="text-sm font-semibold text-[#0F172A]">Uzmanlar hazır</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    ),

    nasil: () => (
      <section key="nasil" id="nasil" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50">{t("nasil_badge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t("nasil_title")}</h2>
            <p className="mt-3 text-slate-600">{t("nasil_sub")}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", title: t("nasil_1_title"), desc: t("nasil_1_desc"), icon: Sparkles },
              { n: "02", title: t("nasil_2_title"), desc: t("nasil_2_desc"), icon: Eye },
              { n: "03", title: t("nasil_3_title"), desc: t("nasil_3_desc"), icon: MessageCircle },
            ].map((s) => (
              <div key={s.n} className="relative rounded-2xl bg-white border border-slate-100 pak-shadow p-7 hover:-translate-y-1 transition-transform duration-300">
                <span className="text-7xl font-extrabold text-emerald-50 absolute top-2 right-4 select-none">{s.n}</span>
                <div className="relative">
                  <div className="h-12 w-12 rounded-2xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center mb-4">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),

    hizmetler: () => (
      <section key="hizmetler" id="hizmetler" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <Badge className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50">{t("hizmetler_badge")}</Badge>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t("hizmetler_title")}</h2>
            </div>
            <p className="text-slate-600 max-w-md">{t("hizmetler_sub")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c) => (
              <Link
                to={`/rezervasyon?service=${c.key}`}
                key={c.key}
                className={`group relative rounded-2xl bg-white border ${c.featured ? "border-[#10B981]/40 ring-1 ring-[#10B981]/10" : "border-slate-100"} pak-shadow p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex flex-col`}
                data-testid={`service-card-${c.key}`}
              >
                {c.featured && (
                  <span className="absolute -top-2.5 left-6 text-[11px] font-semibold bg-[#10B981] text-white px-3 py-1 rounded-full">
                    En Popüler
                  </span>
                )}
                <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-[#10B981] flex items-center justify-center mb-4 group-hover:bg-[#10B981] group-hover:text-white transition-colors">
                  <ServiceIcon name={c.icon} className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold">{c.label}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-sm text-slate-500">
                    <strong className="text-[#0F172A]">₺{c.fromPrice.toLocaleString("tr-TR")}</strong>'den başlayan
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-[#10B981] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),

    neden: () => (
      <section key="neden" className="py-16 md:py-24 bg-gradient-to-b from-emerald-50/40 to-transparent">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="rounded-full bg-white text-[#10B981] border border-emerald-100">{t("neden_badge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t("neden_title")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, title: t("neden_1_title"), desc: t("neden_1_desc") },
              { icon: Tag, title: t("neden_2_title"), desc: t("neden_2_desc") },
              { icon: Eye, title: t("neden_3_title"), desc: t("neden_3_desc") },
              { icon: MessageCircle, title: t("neden_4_title"), desc: t("neden_4_desc") },
            ].map((it, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-100 p-6 pak-shadow">
                <div className="h-11 w-11 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center mb-4">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-[#0F172A]">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),

    saglayicilar: () => (
      featured.length > 0 ? (
      <section key="saglayicilar" id="saglayicilar" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <Badge className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50">{t("saglayicilar_badge")}</Badge>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t("saglayicilar_title")}</h2>
            </div>
            <Link to="/rezervasyon">
              <Button variant="outline" className="rounded-xl border-slate-200 hover:border-[#10B981] hover:text-[#10B981]" data-testid="providers-see-all">
                Tüm uzmanları gör <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <div key={p.id} className="rounded-2xl bg-white border border-slate-100 pak-shadow p-5 hover:-translate-y-1 transition-transform" data-testid={`featured-provider-${p.id}`}>
                <div className="flex items-center gap-3">
                  {p.photo ? (
                    <img src={p.photo} alt={p.name} className="h-14 w-14 rounded-2xl object-cover" />
                  ) : (
                    <div className="h-14 w-14 rounded-2xl bg-emerald-100 text-[#10B981] font-semibold text-xl flex items-center justify-center">
                      {(p.name || "?").charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-[#0F172A]">{p.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.district}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold text-[#0F172A]">{p.rating}</span>
                  <span className="text-slate-500">({p.reviews})</span>
                  <span className="ml-auto text-xs text-slate-500">{p.jobs} iş</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.badges.map((b) => (
                    <Badge key={b} className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50 text-[11px] font-medium border-0">
                      {b}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      ) : null
    ),

    yorumlar: () => (
      realReviews.length > 0 ? (
      <section key="yorumlar" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50">{t("yorumlar_badge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t("yorumlar_title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {realReviews.map((tRev, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-100 pak-shadow p-7 flex flex-col">
                <div className="flex items-center gap-0.5 text-amber-400">
                  {Array.from({ length: tRev.stars }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber-400" />
                  ))}
                </div>
                <p className="mt-4 text-[#0F172A] leading-relaxed flex-1">"{tRev.text}"</p>
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 text-[#10B981] font-semibold flex items-center justify-center">
                    {(tRev.customer_name || "?").charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{tRev.customer_name}</p>
                    <p className="text-xs text-slate-500">{new Date(tRev.created_at).toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      ) : null
    ),

    sss: () => (
      <section key="sss" id="sss" className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-center mb-12">
            <Badge className="rounded-full bg-emerald-50 text-[#10B981] hover:bg-emerald-50">{t("sss_badge")}</Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t("sss_title")}</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-slate-100 rounded-2xl mb-3 px-5 bg-white pak-shadow data-[state=open]:border-[#10B981]/30">
                <AccordionTrigger className="text-left font-semibold text-[#0F172A] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    ),

    cta: () => (
      <section key="cta" className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] p-10 md:p-16 text-white">
            <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-[#10B981]/20 blur-3xl" />
            <div className="absolute -bottom-12 -left-10 h-72 w-72 rounded-full bg-emerald-400/15 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold tracking-tight">
                  {t("cta_title_1")} <span className="text-[#34D399]">{t("cta_title_hl")}</span>{t("cta_title_2")}
                </h3>
                <p className="mt-4 text-slate-300 max-w-md">
                  {t("cta_sub")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                <Link to="/rezervasyon" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto h-14 rounded-2xl bg-[#10B981] hover:bg-[#0EA371] text-white px-8 text-base font-semibold" data-testid="cta-book-btn">
                    {t("cta_button")}
                    <ChevronRight className="ml-1 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Ana sayfa SSS yapısal verisi (görünür #sss bölümüyle eşleşir) */}
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) }} />
      {/* NAV */}
      <header className="native-hide sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <PakLogo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
            <a href="#nasil" className="hover:text-[#10B981] transition" data-testid="nav-how">Nasıl Çalışır</a>
            <a href="#hizmetler" className="hover:text-[#10B981] transition" data-testid="nav-services">Hizmetler</a>
            <a href="#saglayicilar" className="hover:text-[#10B981] transition" data-testid="nav-providers">Sağlayıcılar</a>
            <a href="#sss" className="hover:text-[#10B981] transition" data-testid="nav-faq">Sıkça Sorulanlar</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell />
                <UserMenu />
              </>
            ) : (
              <Link to="/giris" className="inline-flex">
                <Button variant="outline" className="rounded-xl border-slate-200 hover:border-[#10B981] hover:text-[#10B981]" data-testid="nav-login-btn">
                  {t("nav_login")}
                </Button>
              </Link>
            )}
            <Link to="/rezervasyon" className="hidden md:inline-flex">
              <Button className="rounded-xl bg-[#10B981] hover:bg-[#0EA371] text-white px-5 shadow-sm" data-testid="nav-book-btn">
                {t("nav_book")}
              </Button>
            </Link>
            <button
              className="native-hide md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="nav-mobile-toggle"
              aria-label="Menü"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-5 py-4 flex flex-col gap-3 text-sm font-medium">
            <a href="#nasil" onClick={() => setMobileOpen(false)}>Nasıl Çalışır</a>
            <a href="#hizmetler" onClick={() => setMobileOpen(false)}>Hizmetler</a>
            <a href="#saglayicilar" onClick={() => setMobileOpen(false)}>Sağlayıcılar</a>
            <a href="#sss" onClick={() => setMobileOpen(false)}>Sıkça Sorulanlar</a>
          </div>
        )}
      </header>

      {/* Mobil: sağ-altta sabit yüzen "Ücretsiz Talep Oluştur" */}
      <Link
        to="/rezervasyon"
        className="mobile-fab md:hidden fixed right-4 z-50"
        style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
        data-testid="mobile-fab-book"
      >
        <Button className="rounded-2xl bg-[#10B981] hover:bg-[#0EA371] text-white h-14 px-6 text-base font-semibold shadow-xl shadow-emerald-500/30">
          {t("nav_book")} <ChevronRight className="ml-1 h-5 w-5" />
        </Button>
      </Link>

      {/* Bölümler — sıra ve görünürlük admin panelinden yönetilir */}
      {sections.filter((s) => s.enabled).map((s) => {
        const r = renderers[s.key];
        return r ? <Fragment key={s.key}>{r()}</Fragment> : null;
      })}

      {/* Yerel bölge bağlantıları (SEO iç linkleme) */}
      <section className="native-hide bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <h2 className="text-xl font-bold tracking-tight">Şehrinizde hizmetler</h2>
          <p className="mt-1 text-sm text-slate-500">Bölgenizdeki temizlik ve halı/koltuk yıkama sağlayıcılarını görün.</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {LOCAL_SERVICE_LIST.map((s) => (
              <div key={s.slug}>
                <p className="text-sm font-semibold text-slate-700 mb-2">{s.label}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                  {MAJOR_CITIES.map((c) => (
                    <Link key={c} to={`/hizmet/${s.slug}/${slugifyTr(c)}`} className="text-sm text-slate-500 hover:text-[#10B981] hover:underline">
                      {c} {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="native-hide bg-white border-t border-slate-100 pt-14 pb-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div>
              <PakLogo />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                {t("footer_tagline")}
              </p>
              <div className="mt-5 flex items-center gap-3">
                {[Instagram, Twitter, Facebook].map((Icon, i) => (
                  <a key={i} href="#" className="h-9 w-9 rounded-xl bg-slate-100 hover:bg-emerald-50 hover:text-[#10B981] flex items-center justify-center text-slate-600 transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-[#0F172A] mb-4">Hizmetler</p>
              <ul className="space-y-2.5 text-sm text-slate-600">
                {categories.slice(0, 5).map((c) => (
                  <li key={c.key}><a href={`#hizmetler`} className="hover:text-[#10B981]">{c.label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-[#0F172A] mb-4">Şirket</p>
              <ul className="space-y-2.5 text-sm text-slate-600">
                <li><a href="#" className="hover:text-[#10B981]">Hakkımızda</a></li>
                <li><a href="#" className="hover:text-[#10B981]">Uzman olarak çalış</a></li>
                <li><a href="#" className="hover:text-[#10B981]">Blog</a></li>
                <li><a href="#sss" className="hover:text-[#10B981]">Yardım Merkezi</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-[#0F172A] mb-4">İletişim</p>
              <ul className="space-y-2.5 text-sm text-slate-600">
                <li>{t("footer_email")}</li>
                <li>{t("footer_phone")}</li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <p>{t("footer_copyright")}</p>
            <div className="flex gap-5">
              <a href="#" className="hover:text-[#10B981]">Gizlilik</a>
              <a href="#" className="hover:text-[#10B981]">Kullanım Şartları</a>
              <a href="#" className="hover:text-[#10B981]">KVKK</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
